/**
 * ControlConfidenceCard
 *
 * Reusable card that surfaces the confidence posture for a single control.
 * Combines the ConfidenceBadge, top-2 driver indicators, and a trend arrow.
 *
 * Example rendered output:
 *   CC6.1 -- 82 STRONG
 *   "Evidence continuous (7d)" (check)
 *   "Collector lag 2x this week" (warning)
 */
import React from 'react';
import {
  Card,
  Body1,
  Body2,
  Caption1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ConfidenceBadge } from './ConfidenceBadge';
import { semanticColors } from '../../theme/solution-theme';

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  controlId: {
    fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
  },
  controlName: {
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  trendIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase200,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  driversSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  driverRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
  },
  driverIcon: {
    flexShrink: 0,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
  },
  driverText: {
    color: tokens.colorNeutralForeground3,
  },
});

/* ------------------------------------------------------------------ */
/*  Trend helpers                                                      */
/* ------------------------------------------------------------------ */

// #EF4444 (red) and #9CA3AF (grey) are not in brand-config.json's palette \u2014
// no token represents them, so they are left as raw literals.
function trendMeta(trend: 'up' | 'down' | 'stable') {
  switch (trend) {
    case 'up':
      return { symbol: '\u2191', color: semanticColors.success, label: 'Trending up' };
    case 'down':
      return { symbol: '\u2193', color: '#EF4444', label: 'Trending down' };
    case 'stable':
    default:
      return { symbol: '\u2192', color: '#9CA3AF', label: 'Stable' };
  }
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ControlConfidenceDriver {
  /** Human-readable driver description. */
  text: string;
  /** True = positive signal, false = concern / warning. */
  positive: boolean;
}

export interface ControlConfidenceCardProps {
  /** Control identifier, e.g. "CC6.1". */
  controlId: string;
  /** Display name of the control. */
  controlName: string;
  /** Confidence score 0-100. */
  score: number;
  /** Top driver observations (only first 2 are rendered). */
  drivers: ControlConfidenceDriver[];
  /** Score trend direction. @default 'stable' */
  trend?: 'up' | 'down' | 'stable';
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ControlConfidenceCard: React.FC<ControlConfidenceCardProps> = ({
  controlId,
  controlName,
  score,
  drivers,
  trend = 'stable',
}) => {
  const styles = useStyles();
  const tm = trendMeta(trend);
  const topDrivers = drivers.slice(0, 2);

  return (
    <Card className={styles.card} aria-label={`${controlId} confidence`}>
      {/* Header: ID + name | badge + trend */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Body1 className={styles.controlId}>{controlId}</Body1>
          <Body2 className={styles.controlName}>{controlName}</Body2>
        </div>
        <div className={styles.titleRow}>
          <ConfidenceBadge score={score} size="medium" />
          <span
            className={styles.trendIndicator}
            style={{ color: tm.color }}
            title={tm.label}
            aria-label={tm.label}
          >
            {tm.symbol}
          </span>
        </div>
      </div>

      {/* Driver observations */}
      {topDrivers.length > 0 && (
        <div className={styles.driversSection}>
          {topDrivers.map((d, i) => (
            <div key={i} className={styles.driverRow}>
              <span
                className={styles.driverIcon}
                style={{ color: d.positive ? '#22C55E' : '#F59E0B' }}
                aria-hidden="true"
              >
                {d.positive ? '\u2713' : '\u26A0'}
              </span>
              <Caption1 className={styles.driverText}>{d.text}</Caption1>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
